import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import './ProjectKanban.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const kanbanSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const DEFAULT_COLUMNS = [
  { id: 'todo', title: 'To Do', color: '#94a3b8' },
  { id: 'in_progress', title: 'In Progress', color: '#f59e0b' },
  { id: 'review', title: 'Review', color: '#8b5cf6' },
  { id: 'done', title: 'Done', color: '#22c55e' },
];

// Task card contents: title (required), description (optional), due_date, assignee_name

export default function ProjectKanban() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const justDraggedRef = useRef(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [projectMembers, setProjectMembers] = useState([]);
  const [addMemberId, setAddMemberId] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [taskError, setTaskError] = useState(null);
  const mountedRef = useRef(true);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      kanbanSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const fetchPromise = kanbanSupabase.from('projects').select('*').eq('id', projectId).single();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      const { data, error: err } = result;
      if (err) {
        setError(err.message || 'Project not found');
        setProject(null);
      } else {
        setProject(data);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load project');
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchProfiles = useCallback(async () => {
    const { data, error: err } = await kanbanSupabase
      .from('profiles')
      .select('id, full_name')
      .order('full_name', { ascending: true, nullsFirst: false });
    if (!err) setProfiles(data || []);
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!projectId) return;
    const { data, error: err } = await kanbanSupabase
      .from('tasks')
      .select('*')
      .eq('project_id', projectId)
      .order('position', { ascending: true });
    if (!err && Array.isArray(data)) setTasks(data);
    else setTasks([]);
  }, [projectId]);

  const tasksByColumn = DEFAULT_COLUMNS.reduce((acc, col) => {
    acc[col.id] = (tasks || []).filter((t) => (t.status || 'todo') === col.id);
    return acc;
  }, {});

  const goBack = () => navigate('/taskboard');

  const fetchProjectMembers = useCallback(async () => {
    if (!projectId) return;
    const { data, error: err } = await kanbanSupabase
      .from('project_members')
      .select('user_id')
      .eq('project_id', projectId);
    if (err) {
      setProjectMembers([]);
      return;
    }
    const userIds = (data || []).map((r) => r.user_id);
    const members = userIds.map((user_id) => ({
      user_id,
      full_name: profiles.find((p) => p.id === user_id)?.full_name || user_id,
    }));
    setProjectMembers(members);
  }, [projectId, profiles]);

  const refreshSessionAndFetch = useCallback(async () => {
    if (!projectId) return;
    mountedRef.current = true;
    setError(null);
    try {
      await kanbanSupabase.auth.getSession();
      await runWakeUp();
      await fetchProject();
      if (!mountedRef.current) return;
      await fetchProfiles();
      await fetchTasks();
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [projectId, runWakeUp, fetchProject, fetchProfiles, fetchTasks]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await kanbanSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchProject();
      if (cancelled) return;
      await fetchProfiles();
      await fetchTasks();
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [projectId, runWakeUp, fetchProject, fetchProfiles, fetchTasks]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  useEffect(() => {
    if (projectId && profiles.length > 0) fetchProjectMembers();
  }, [projectId, profiles.length, fetchProjectMembers]);

  const handleOpenMembersModal = () => {
    setShowMembersModal(true);
    setAddMemberId('');
    fetchProjectMembers();
  };

  const handleAddMember = async () => {
    if (!addMemberId || !projectId) return;
    setAddingMember(true);
    try {
      await runWakeUp();
      const { error: err } = await kanbanSupabase
        .from('project_members')
        .insert({ project_id: projectId, user_id: addMemberId });
      if (!err) {
        await fetchProjectMembers();
        setAddMemberId('');
      }
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!projectId) return;
    await runWakeUp();
    await kanbanSupabase
      .from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId);
    fetchProjectMembers();
  };

  const memberIds = projectMembers.map((m) => m.user_id);
  const profilesToAdd = profiles.filter((p) => !memberIds.includes(p.id));

  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: task.id, status: task.status || 'todo' }));
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    setDraggedTaskId(task.id);
  };

  const handleDragEnd = () => {
    setDraggedTaskId(null);
    setDragOverColumnId(null);
    justDraggedRef.current = true;
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnId(columnId);
  };

  const handleDragLeave = (e) => {
    const related = e.relatedTarget;
    if (!related || !e.currentTarget.contains(related)) {
      setDragOverColumnId(null);
    }
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    setDragOverColumnId(null);
    try {
      const raw = e.dataTransfer.getData('text/plain');
      const { id: taskId, status: fromStatus } = JSON.parse(raw);
      if (fromStatus === targetColumnId) return;

      const isTempId = typeof taskId === 'string' && taskId.startsWith('new-');
      if (isTempId) {
        setTaskError('This task hasn’t been saved to the server yet. Open it and save, or refresh the page.');
        return;
      }

      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId ? { ...t, status: targetColumnId } : t
        )
      );
      setDraggedTaskId(null);
      justDraggedRef.current = true;
      runWakeUp().then(() =>
        kanbanSupabase
          .from('tasks')
          .update({ status: targetColumnId })
          .eq('id', taskId)
          .select('id')
          .single()
      ).then((res) => {
        const err = res?.error;
          if (err) {
            setTaskError(err.message || 'Failed to move task.');
            fetchTasks();
          } else {
            setTaskError(null);
            fetchTasks();
          }
        });
    } catch {
      setDraggedTaskId(null);
    }
  };

  const handleOpenTask = (task) => {
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    const assigneeId =
      task.assignee_profile_id ||
      (task.assignee_name && projectMembers.find((m) => m.full_name === task.assignee_name)?.user_id) ||
      null;
    setSelectedTask({
      ...task,
      assignee_profile_id: assigneeId,
      comments: Array.isArray(task.comments) ? [...task.comments] : [],
    });
    setNewComment('');
    setTaskError(null);
  };

  const handleCloseTask = () => {
    setSelectedTask(null);
    setNewComment('');
  };

  const handleTaskChange = (field, value) => {
    setSelectedTask((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleAddComment = () => {
    const text = newComment.trim();
    if (!text || !selectedTask) return;
    const comment = { id: `c-${Date.now()}`, text, created_at: new Date().toISOString() };
    setSelectedTask((prev) =>
      prev ? { ...prev, comments: [...(prev.comments || []), comment] } : null
    );
    setNewComment('');
  };

  const handleSaveTask = async () => {
    if (!selectedTask) return;
    setSavingTask(true);
    const member = projectMembers.find((m) => m.user_id === selectedTask.assignee_profile_id);
    const updated = {
      ...selectedTask,
      assignee_name: member ? member.full_name : (selectedTask.assignee_profile_id ? selectedTask.assignee_name : null),
    };
    setTasks((prev) =>
      prev.map((t) => (t.id === selectedTask.id ? updated : t))
    );
    setSelectedTask(updated);
    try {
      await runWakeUp();
      const { error: err } = await kanbanSupabase
        .from('tasks')
        .update({
          title: updated.title,
          description: updated.description || null,
          due_date: updated.due_date || null,
          assignee_profile_id: updated.assignee_profile_id || null,
          assignee_name: updated.assignee_name || null,
          comments: updated.comments || [],
        })
        .eq('id', updated.id);
      if (err) {
        setTaskError(err.message || 'Failed to save task.');
      } else {
        setTaskError(null);
        await fetchTasks();
      }
    } catch (e) {
      setTaskError(e?.message || 'Failed to save task.');
    } finally {
      setSavingTask(false);
    }
  };

  const handleAddTask = async () => {
    const title = newTaskTitle.trim();
    if (!title || !projectId) return;
    setAddingTask(true);
    const tempId = `new-${Date.now()}`;
    const newTask = {
      id: tempId,
      title,
      description: null,
      due_date: null,
      assignee_name: null,
      status: 'todo',
      project_id: projectId,
      position: (tasks.filter((t) => t.status === 'todo').length) || 0,
    };
    setTasks((prev) => [...prev, newTask]);
    setNewTaskTitle('');
    setShowAddTask(false);
    try {
      await runWakeUp();
      const { data, error: err } = await kanbanSupabase
        .from('tasks')
        .insert({
          project_id: projectId,
          title,
          status: 'todo',
          position: newTask.position,
        })
        .select()
        .single();
      if (!err && data) {
        setTaskError(null);
        setTasks((prev) => prev.map((t) => (t.id === tempId ? data : t)));
      } else {
        setTaskError(err?.message || 'Failed to create task.');
        await fetchTasks();
      }
    } catch (e) {
      setTaskError(e?.message || 'Failed to create task.');
      await fetchTasks();
    } finally {
      setAddingTask(false);
    }
  };

  if (loading) {
    return (
      <div className="kanban-page">
        <div className="kanban-loading">Loading board…</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="kanban-page">
        <div className="kanban-error" role="alert">
          {error || 'Project not found'}
        </div>
        <button type="button" className="kanban-back-btn" onClick={goBack}>
          ← Back to Taskboard
        </button>
      </div>
    );
  }

  return (
    <div className="kanban-page">
      <header className="kanban-header">
        <button
          type="button"
          className="kanban-back-btn"
          onClick={goBack}
          aria-label="Back to Taskboard"
        >
          ← Back
        </button>
        <div className="kanban-header-title-wrap">
          <h1 className="kanban-project-title">{project.title || 'Untitled project'}</h1>
          {project.client_name && (
            <span className="kanban-project-client">{project.client_name}</span>
          )}
        </div>
        <button
          type="button"
          className="kanban-header-add-members-btn"
          onClick={handleOpenMembersModal}
        >
          Add members
        </button>
      </header>
      {taskError && (
        <div className="kanban-task-error" role="alert">
          {taskError}
          <button type="button" className="kanban-task-error-dismiss" onClick={() => setTaskError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="kanban-board">
        {DEFAULT_COLUMNS.map((col) => (
          <section key={col.id} className="kanban-column">
            <div
              className="kanban-column-header"
              style={{ borderTopColor: col.color }}
            >
              <h2 className="kanban-column-title">{col.title}</h2>
              <div className="kanban-column-header-right">
                <span className="kanban-column-count">
                  {(tasksByColumn[col.id] || []).length}
                </span>
                {col.id === 'todo' && (
                  <button
                    type="button"
                    className="kanban-column-add-btn"
                    onClick={() => setShowAddTask(true)}
                    aria-label="Add task"
                    title="Add task"
                  >
                    +
                  </button>
                )}
              </div>
            </div>
            <div
              className={`kanban-column-cards ${dragOverColumnId === col.id ? 'kanban-column-cards--drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col.id)}
            >
              {col.id === 'todo' && showAddTask && (
                <div className="kanban-add-card-form">
                  <input
                    type="text"
                    className="kanban-add-card-input"
                    placeholder="Task title"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTask();
                      if (e.key === 'Escape') {
                        setShowAddTask(false);
                        setNewTaskTitle('');
                      }
                    }}
                    autoFocus
                  />
                  <div className="kanban-add-card-actions">
                    <button
                      type="button"
                      className="kanban-add-card-submit"
                      onClick={handleAddTask}
                      disabled={addingTask || !newTaskTitle.trim()}
                    >
                      {addingTask ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      className="kanban-add-card-cancel"
                      onClick={() => {
                        setShowAddTask(false);
                        setNewTaskTitle('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {(tasksByColumn[col.id] || []).length === 0 && !(col.id === 'todo' && showAddTask) ? (
                <div className="kanban-column-empty">No tasks</div>
              ) : (tasksByColumn[col.id] || []).length > 0 ? (
                (tasksByColumn[col.id] || []).map((task) => (
                  <div
                    key={task.id}
                    className={`kanban-card ${draggedTaskId === task.id ? 'kanban-card--dragging' : ''}`}
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleOpenTask(task)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenTask(task);
                      }
                    }}
                  >
                    <h3 className="kanban-card-title">{task.title || 'Untitled task'}</h3>
                    {task.description && (
                      <p className="kanban-card-desc">{task.description}</p>
                    )}
                    <div className="kanban-card-footer">
                      {task.due_date && (
                        <span className="kanban-card-due" title="Due date">
                          <span className="kanban-card-due-icon" aria-hidden>📅</span>
                          {task.due_date}
                        </span>
                      )}
                      {task.assignee_name && (
                        <span className="kanban-card-assignee" title="Assignee">
                          <span className="kanban-card-assignee-initial" aria-hidden>
                            {(task.assignee_name || '?').charAt(0).toUpperCase()}
                          </span>
                          {task.assignee_name}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : null}
            </div>
          </section>
        ))}
      </div>

      {showMembersModal && (
        <div
          className="kanban-task-modal-backdrop"
          onClick={() => setShowMembersModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="kanban-members-modal-title"
        >
          <div
            className="kanban-task-modal kanban-members-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="kanban-task-modal-header">
              <h2 id="kanban-members-modal-title" className="kanban-task-modal-title">
                Project members
              </h2>
              <button
                type="button"
                className="kanban-task-modal-close"
                onClick={() => setShowMembersModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="kanban-task-modal-body">
              <div className="kanban-members-list">
                {projectMembers.length === 0 ? (
                  <p className="kanban-members-empty">No members added yet.</p>
                ) : (
                  <ul className="kanban-members-ul">
                    {projectMembers.map((m) => (
                      <li key={m.user_id} className="kanban-members-item">
                        <span>{m.full_name}</span>
                        <button
                          type="button"
                          className="kanban-members-remove"
                          onClick={() => handleRemoveMember(m.user_id)}
                          aria-label={`Remove ${m.full_name}`}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="kanban-members-add">
                <label className="kanban-task-label">Add member</label>
                <div className="kanban-members-add-row">
                  <select
                    className="kanban-task-select"
                    value={addMemberId}
                    onChange={(e) => setAddMemberId(e.target.value)}
                  >
                    <option value="">— Select user —</option>
                    {profilesToAdd.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.full_name || p.id}
                      </option>
                    ))}
                    {profilesToAdd.length === 0 && (
                      <option value="" disabled>All users already added</option>
                    )}
                  </select>
                  <button
                    type="button"
                    className="kanban-members-add-btn"
                    onClick={handleAddMember}
                    disabled={!addMemberId || addingMember}
                  >
                    {addingMember ? 'Adding…' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
            <div className="kanban-task-modal-footer">
              <button
                type="button"
                className="kanban-task-footer-save"
                onClick={() => setShowMembersModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTask && (
        <div
          className="kanban-task-modal-backdrop"
          onClick={handleCloseTask}
          role="dialog"
          aria-modal="true"
          aria-labelledby="kanban-task-modal-title"
        >
          <div
            className="kanban-task-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="kanban-task-modal-header">
              <h2 id="kanban-task-modal-title" className="kanban-task-modal-title">
                Task details
              </h2>
              <button
                type="button"
                className="kanban-task-modal-close"
                onClick={handleCloseTask}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="kanban-task-modal-body">
              <label className="kanban-task-field">
                <span className="kanban-task-label">Title</span>
                <input
                  type="text"
                  className="kanban-task-input"
                  value={selectedTask.title || ''}
                  onChange={(e) => handleTaskChange('title', e.target.value)}
                  placeholder="Task title"
                />
              </label>
              <label className="kanban-task-field">
                <span className="kanban-task-label">Description</span>
                <textarea
                  className="kanban-task-textarea"
                  value={selectedTask.description || ''}
                  onChange={(e) => handleTaskChange('description', e.target.value)}
                  placeholder="Add description…"
                  rows={3}
                />
              </label>
              <label className="kanban-task-field">
                <span className="kanban-task-label">Deadline</span>
                <input
                  type="date"
                  className="kanban-task-input"
                  value={selectedTask.due_date || ''}
                  onChange={(e) => handleTaskChange('due_date', e.target.value)}
                />
              </label>
              <label className="kanban-task-field">
                <span className="kanban-task-label">Assignee</span>
                <select
                  className="kanban-task-select"
                  value={selectedTask.assignee_profile_id || ''}
                  onChange={(e) => handleTaskChange('assignee_profile_id', e.target.value || null)}
                >
                  <option value="">— No assignee —</option>
                  {projectMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.full_name || m.user_id}
                    </option>
                  ))}
                  {projectMembers.length === 0 && (
                    <option value="" disabled>Add project members first</option>
                  )}
                </select>
              </label>
              <div className="kanban-task-field">
                <span className="kanban-task-label">Comments</span>
                <ul className="kanban-task-comments-list">
                  {(selectedTask.comments || []).map((c) => (
                    <li key={c.id || c.text} className="kanban-task-comment">
                      {c.text}
                    </li>
                  ))}
                  {(selectedTask.comments || []).length === 0 && (
                    <li className="kanban-task-comment-empty">No comments yet</li>
                  )}
                </ul>
                <div className="kanban-task-comment-add">
                  <input
                    type="text"
                    className="kanban-task-input"
                    placeholder="Add a comment…"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <button
                    type="button"
                    className="kanban-task-comment-btn"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            <div className="kanban-task-modal-footer">
              <button
                type="button"
                className="kanban-task-footer-cancel"
                onClick={handleCloseTask}
              >
                Cancel
              </button>
              <button
                type="button"
                className="kanban-task-footer-save"
                onClick={handleSaveTask}
                disabled={savingTask}
              >
                {savingTask ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
